import { MoveUp } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { Fragment } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ConfirmAndCancelButtons from "../../../../../../components/buttons/ConfirmAndCancelButtons";
import { SnackbarContext } from "../../../../../../contexts/Snackbar";
import {
  AVAILABLE_BOXES_FOR_MOVE,
  MOVE_PET_TO_BOX,
} from "../../../../../../enums/ApiPaths";
import { useFetch } from "../../../../../../hooks/Fetching";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";
import useSubmit from "../../../../../master/species/hooks/useSubmit";
import {
  MOVE_PET_AVAILABE_BOXES,
  SINGLE_BOX_KEY,
} from "../../../../../../enums/QueryKeys";
import NotFound, { ErrorMessage } from "../../../../components/display/NotFound";

export default function MovePetFromBox({ pet }) {
  const { t } = useTranslation();
  const { id: currentBoxId } = useParams();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [openDialog, setOpenDialog] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { newBoxId: "" } });

  const boxKey = SINGLE_BOX_KEY + currentBoxId;
  const submittion = useSubmit(null, MOVE_PET_TO_BOX, boxKey, [boxKey]);

  const { data: availableBoxes = [], isLoading: loadingBoxes } = useFetch(
    AVAILABLE_BOXES_FOR_MOVE,
    pet?.id,
    [MOVE_PET_AVAILABE_BOXES, pet?.id],
    openDialog,
  );

  function handleDialog() {
    setOpenDialog((prev) => !prev);
    if (openDialog) reset();
  }

  async function onSubmit(data) {
    const res = await submittion.submitCallback({
      currentBoxId: Number(currentBoxId),
      newBoxId: Number(data.newBoxId),
      petId: Number(pet?.id),
    });

    if (res) {
      setOpenDialog(false);
      reset();
      handleSnackbar("PetMovedSuccessfully", "success");
    }
  }

  const noBoxes = !availableBoxes || availableBoxes.length == 0;

  return (
    <>
      <ActionRow
        icon={<MoveUp />}
        label={t("MoveFromBox")}
        description={t("MoveFromBoxDescription")}
        onClick={handleDialog}
        disabled={submittion.loading}
        loading={submittion.loading}
      />

      <Dialog
        open={openDialog}
        onClose={handleDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3} }}
      >
        {noBoxes ? (
          <Fragment>
            <Header handleDialog={handleDialog}/>
            <NotFound
              title={t("NoBoxesAvailable")}
              subtitle={t("AddMoreBoxesFirst")}
            />
          </Fragment>
        ) : (
          <Fragment>
            <Header handleDialog={handleDialog}/>

            <form onSubmit={handleSubmit(onSubmit)}>
              <DialogContent>
                <Stack spacing={2}>
                  <Alert severity="info">
                    {t("MovePetFromBoxInfo", { name: pet?.name })}
                  </Alert>

                  {loadingBoxes ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 2 }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Controller
                      name="newBoxId"
                      control={control}
                      rules={{ required: t("EmptyField") }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.newBoxId}>
                          <InputLabel>{t("SelectBox")}</InputLabel>
                          <Select {...field} label={t("SelectBox")}>
                            {availableBoxes.map((box) => (
                              <MenuItem key={box.id} value={box.id}>
                                {box.name} —{" "}
                                {box.numberOfPets === 1
                                  ? t("petsCount", { count: 1 })
                                  : t("petsCount_plural", {
                                      count: box.numberOfPets,
                                    })}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {errors.newBoxId?.message}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  )}
                </Stack>
              </DialogContent>

              <DialogActions
                sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
              >
                <ConfirmAndCancelButtons
                  confirmTitle="Move"
                  cancelTitle="Cancel"
                  onCancel={handleDialog}
                  confirmButtonType="submit"
                  isConfirmLoading={submittion.loading}
                  confirmButtonProps={{
                    disabled: submittion.loading || loadingBoxes,
                  }}
                />
              </DialogActions>
            </form>
          </Fragment>
        )}
      </Dialog>
    </>
  );
}

function Header({ handleDialog }) {
  const {t} = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        py: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <MoveUp color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700}>
          {t("MoveFromBox")}
        </Typography>
      </Stack>
      <IconButton onClick={handleDialog} size="small" color="error" title={t("Close")}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
